// Pure geometry behind the country names: which way a territory runs, how a name spreads along it,
// and which ink reads on a plate colour.
//
//    territory hexes ──▶ principalAxis ──▶ direction + length along it
//    axis length on screen ──▶ fitLabel ──▶ font size + letter spacing that fill the span
//
//         ┌ R   U   S   S   I   A ┐   spread over the territory, tilted with it (never past
//         └───────────────────────┘   MAX_TILT, so the text stays readable)

import { HEX_W, WORLD_W, hexCenter, hexCol, hexRow } from '../../core/grid.ts';

const HALF = 0.5;
const EPSILON = 1e-9;
const DEG = Math.PI / 180;

// A territory rounder than this (major / minor spread) has no direction worth following.
const ELONGATED = 1.35;
// Territories running steeper than FLAT keep horizontal text; shallower ones tilt up to MAX_TILT.
const FLAT = 60 * DEG;
const MAX_TILT = 35 * DEG;

// Name size in px, and letter spacing in em: names spread before they grow.
const SIZE_MIN = 11;
const SIZE_MAX = 30;
const SPACING_MIN = 0.08;
const SPACING_MAX = 0.7;
const FILL = 0.8; // share of the span a name may use

const SRGB_KNEE = 0.03928;
const LIGHT_INK = '#fff6de';
const DARK_INK = '#14202f';
const DARK_INK_ABOVE = 0.42; // plate luminance above which dark ink reads better

/** Direction a label runs and how far the territory extends along it. */
export interface Axis {
  /** Unit direction in world XZ (x east, z south); x is never negative. */
  x: number;
  z: number;
  /** Extent of the territory along the direction, in world units. */
  length: number;
}

/** How a name is set: font size (px), letter spacing (em) and the resulting width (px). */
export interface LabelFit {
  size: number;
  spacing: number;
  width: number;
}

/**
 * Direction of a territory (least-squares line through its hex centres) and its extent along it.
 * `unwrappedX` holds each hex's x continuous across the date line (a territory may straddle it).
 */
export function principalAxis(hexes: readonly number[], unwrappedX: ArrayLike<number>): Axis {
  const zs = hexes.map((h) => hexCenter(hexCol(h), hexRow(h)).z);
  const xs = hexes.map((h) => unwrappedX[h]);
  const n = hexes.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const mz = zs.reduce((a, b) => a + b, 0) / n;

  let sxx = 0;
  let szz = 0;
  let sxz = 0;
  for (let i = 0; i < n; i++) {
    sxx += (xs[i] - mx) ** 2;
    szz += (zs[i] - mz) ** 2;
    sxz += (xs[i] - mx) * (zs[i] - mz);
  }

  const mean = (sxx + szz) / 2;
  const spread = Math.hypot((sxx - szz) / 2, sxz);
  const minor = mean - spread;
  const elongation = minor <= EPSILON ? (mean > EPSILON ? Infinity : 1) : Math.sqrt((mean + spread) / minor);
  const theta = HALF * Math.atan2(2 * sxz, sxx - szz);
  const tilt = elongation < ELONGATED || Math.abs(theta) > FLAT ? 0 : Math.max(-MAX_TILT, Math.min(MAX_TILT, theta));

  const x = Math.cos(tilt);
  const z = Math.sin(tilt);
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < n; i++) {
    const along = xs[i] * x + zs[i] * z;
    lo = Math.min(lo, along);
    hi = Math.max(hi, along);
  }

  return { x, z, length: Math.min(WORLD_W * HALF, hi - lo + HEX_W) };
}

/**
 * Size and spacing that set a name of `chars` letters, `baseEm` wide unspaced (in em), across
 * `span` px; null when it would fall under the smallest legible size.
 */
export function fitLabel(span: number, chars: number, baseEm: number): LabelFit | null {
  const target = span * FILL;
  const size = Math.min(SIZE_MAX, target / (baseEm + chars * SPACING_MIN));
  if (!(size >= SIZE_MIN)) {
    return null;
  }

  const spacing = Math.max(SPACING_MIN, Math.min(SPACING_MAX, (target / size - baseEm) / chars));
  return { size, spacing, width: size * (baseEm + chars * spacing) };
}

/** Half extents of a `width` × `height` box turned by `angle` (rad): for screen-space declutter. */
export function turnedHalf(width: number, height: number, angle: number): { hw: number; hh: number } {
  const c = Math.abs(Math.cos(angle));
  const s = Math.abs(Math.sin(angle));
  return { hw: (c * width + s * height) * HALF, hh: (s * width + c * height) * HALF };
}

/** Ink colour (css) that reads on a `#rrggbb` plate. */
export function inkOn(plate: string): string {
  const v = Number.parseInt(plate.slice(1), 16);
  const channel = (shift: number): number => {
    const c = ((v >> shift) & 0xff) / 0xff;
    return c <= SRGB_KNEE ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
  return luminance > DARK_INK_ABOVE ? DARK_INK : LIGHT_INK;
}
